import React, { useEffect, useState } from 'react';
import { ticketsAPI } from '../services/api';
import { formatDateTimeEST } from '../utils/dateUtils';

interface TicketsModalProps {
  eventCustomerUuid: string | null;
  isOpen: boolean;
  onClose: () => void;
  customerName?: string;
}

interface Ticket {
  uuid: string;
  ticket_number: string;
  ticket_type: string;
  scanned: boolean;
  scanned_at: string | null;
  generated_at: string;
  qr_code_data: string;
  customer_name: string;
  email: string;
  event_title: string;
  event_address: string;
  event_start_date: string;
  event_end_date: string;
}

const TicketsModal: React.FC<TicketsModalProps> = ({ 
  eventCustomerUuid, 
  isOpen, 
  onClose,
  customerName 
}) => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && eventCustomerUuid) {
      setLoading(true);
      setError('');
      ticketsAPI.getByEventCustomer(eventCustomerUuid)
        .then((response) => {
          setTickets(response.data.tickets || []);
        })
        .catch((err: any) => {
          setError(err.response?.data?.error || 'Failed to load tickets');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setTickets([]);
      setError('');
    }
  }, [isOpen, eventCustomerUuid]);

  const handlePrint = () => {
    window.print();
  };

  if (!isOpen) return null;

  const eventInfo = tickets.length > 0 ? tickets[0] : null;

  return (
    <>
      {/* Print Styles */}
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            #print-area, #print-area * {
              visibility: visible;
            }
            #print-area {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
            }
            .no-print {
              display: none !important;
            }
            .print-ticket {
              page-break-after: always;
              page-break-inside: avoid;
            }
            .print-ticket:last-child {
              page-break-after: auto;
            }
          }
        `}
      </style>

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 no-print">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="bg-gradient-to-r from-primary-600 to-secondary-600 px-8 py-6 rounded-t-2xl">
            <h2 className="text-2xl font-bold text-white">
              Tickets for {customerName || eventInfo?.customer_name || 'Customer'}
            </h2>
          </div>

          <div className="p-8">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
                <p className="text-secondary-600 mt-4">Loading tickets...</p>
              </div>
            ) : error ? (
              <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded">
                {error}
              </div>
            ) : tickets.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-secondary-600 text-lg">No tickets found for this customer.</p>
                <p className="text-secondary-500 mt-2 text-sm">
                  Tickets may not have been generated yet.
                </p>
              </div>
            ) : (
              <>
                {/* Event Info */}
                {eventInfo && (
                  <div className="mb-6 p-4 bg-secondary-50 rounded-lg">
                    <h3 className="text-xl font-bold text-secondary-800 mb-2">{eventInfo.event_title}</h3>
                    <p className="text-secondary-700"><strong>Customer:</strong> {eventInfo.customer_name}</p>
                    <p className="text-secondary-700"><strong>Email:</strong> {eventInfo.email}</p>
                    <p className="text-secondary-700"><strong>Location:</strong> {eventInfo.event_address}</p>
                    <p className="text-secondary-700"><strong>Start:</strong> {formatDateTimeEST(eventInfo.event_start_date)}</p>
                    <p className="text-secondary-700"><strong>End:</strong> {formatDateTimeEST(eventInfo.event_end_date)}</p>
                  </div>
                )}

                {/* Tickets Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {tickets.map((ticket) => (
                    <div 
                      key={ticket.uuid} 
                      className={`border-2 rounded-lg p-4 ${
                        ticket.scanned 
                          ? 'border-red-300 bg-red-50' 
                          : 'border-green-300 bg-green-50'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="font-bold text-lg text-secondary-800">
                            {ticket.ticket_number}
                          </p>
                          <p className="text-sm text-secondary-600 capitalize">
                            {ticket.ticket_type} Ticket
                          </p>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-bold ${
                          ticket.scanned 
                            ? 'bg-red-200 text-red-800' 
                            : 'bg-green-200 text-green-800'
                        }`}>
                          {ticket.scanned ? 'SCANNED' : 'VALID'}
                        </span>
                      </div>
                      
                      {/* QR Code */}
                      {ticket.qr_code_data && (
                        <div className="flex justify-center mb-3 bg-white p-2 rounded">
                          <img 
                            src={ticket.qr_code_data} 
                            alt={`QR Code for ${ticket.ticket_number}`}
                            className="w-40 h-40"
                          />
                        </div>
                      )}
                      
                      <div className="text-xs text-secondary-600 mt-2">
                        <p><strong>Generated:</strong> {formatDateTimeEST(ticket.generated_at)}</p>
                        {ticket.scanned && ticket.scanned_at && (
                          <p><strong>Scanned:</strong> {formatDateTimeEST(ticket.scanned_at)}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Print Button */}
                <div className="flex gap-4 mt-6 no-print">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 px-6 py-3 bg-secondary-200 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-300 transition-all"
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={handlePrint}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-primary-600 to-secondary-600 text-white rounded-lg font-semibold hover:from-primary-700 hover:to-secondary-700 transition-all flex items-center justify-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                    </svg>
                    Print Tickets
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Print Area - Hidden on screen, visible when printing */}
      <div id="print-area" className="hidden print:block">
        {tickets.length > 0 && (
          <div className="p-8">
            <h1 className="text-3xl font-bold text-center mb-6">
              {eventInfo?.event_title}
            </h1>
            
            <div className="mb-6 text-center">
              <p><strong>Customer:</strong> {eventInfo?.customer_name}</p>
              <p><strong>Email:</strong> {eventInfo?.email}</p>
              <p><strong>Location:</strong> {eventInfo?.event_address}</p>
              <p><strong>Event Start:</strong> {eventInfo && formatDateTimeEST(eventInfo.event_start_date)}</p>
            </div>

            {tickets.map((ticket, index) => (
              <div key={ticket.uuid} className="print-ticket mb-8 border-2 border-gray-300 p-6 rounded-lg">
                <div className="text-center">
                  <h2 className="text-2xl font-bold mb-2">{ticket.ticket_number}</h2>
                  <p className="text-lg mb-4 capitalize">{ticket.ticket_type} Ticket</p>
                  
                  {ticket.qr_code_data && (
                    <div className="flex justify-center mb-4">
                      <img 
                        src={ticket.qr_code_data} 
                        alt={`QR Code for ${ticket.ticket_number}`}
                        className="w-64 h-64"
                      />
                    </div>
                  )}
                  
                  <div className="mt-4 text-sm">
                    <p><strong>Status:</strong> {ticket.scanned ? 'SCANNED' : 'VALID'}</p>
                    <p><strong>Generated:</strong> {formatDateTimeEST(ticket.generated_at)}</p>
                    {ticket.scanned && ticket.scanned_at && (
                      <p><strong>Scanned:</strong> {formatDateTimeEST(ticket.scanned_at)}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default TicketsModal;
